import { COUNTRIES } from './timezones.js';
export { COUNTRIES };
/**
 * The zone everything falls back to when nothing else is known. Only reached
 * on a runtime with no `Intl` timezone data at all — every browser and both
 * mobile runtimes resolve a real zone.
 */
export const FALLBACK_TIMEZONE = 'UTC';
/**
 * `UTC` belongs to no country, so it is not in the generated table. It is
 * still a legitimate choice — distributed teams pick it deliberately — so the
 * pickers offer it above the country list.
 */
export const UTC_ZONE = 'UTC';
const ZONE_TO_COUNTRY = new Map();
for (const country of COUNTRIES) {
    for (const zone of country.zones) {
        // First country wins: a zone appears under one country in tzdata, and the
        // rare shared ones (Europe/Zurich for Büsingen) belong to the first.
        if (!ZONE_TO_COUNTRY.has(zone))
            ZONE_TO_COUNTRY.set(zone, country.code);
    }
}
const COUNTRY_BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]));
/** Every canonical zone the pickers offer, `UTC` first. */
export const ALL_TIMEZONES = [
    UTC_ZONE,
    ...[...ZONE_TO_COUNTRY.keys()].sort(),
];
const validCache = new Map();
/**
 * Whether a runtime can actually format in this zone.
 *
 * Deliberately not a check against `ALL_TIMEZONES`: tzdata's backward links
 * ("US/Eastern", "Asia/Calcutta") are still valid input that older clients and
 * phone locales hand us, and rejecting them would throw away a correct
 * preference just because it is spelled the old way.
 */
export function isValidTimezone(zone) {
    if (!zone)
        return false;
    const cached = validCache.get(zone);
    if (cached !== undefined)
        return cached;
    let ok;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: zone }).format();
        ok = true;
    }
    catch {
        ok = false;
    }
    validCache.set(zone, ok);
    return ok;
}
/** The zone this device is in, per the OS. */
export function detectTimezone() {
    try {
        const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
        return zone && isValidTimezone(zone) ? zone : FALLBACK_TIMEZONE;
    }
    catch {
        return FALLBACK_TIMEZONE;
    }
}
/**
 * Which country a zone sits in, as an ISO 3166 code.
 *
 * Undefined for `UTC` and for backward-link names that are not in the table —
 * callers treat that as "no country selected" rather than an error.
 */
export function countryOfTimezone(zone) {
    return ZONE_TO_COUNTRY.get(zone);
}
/**
 * This device's country, guessed from its zone and then from its locale.
 *
 * A guess, not a fact: the two disagree for anyone travelling, which is why
 * the setting is a preselected dropdown rather than something applied silently.
 */
export function detectCountry() {
    const fromZone = countryOfTimezone(detectTimezone());
    if (fromZone)
        return fromZone;
    try {
        const region = new Intl.Locale(new Intl.DateTimeFormat().resolvedOptions().locale).region;
        return region && COUNTRY_BY_CODE.has(region) ? region : undefined;
    }
    catch {
        return undefined;
    }
}
export function findCountry(code) {
    return code ? COUNTRY_BY_CODE.get(code) : undefined;
}
/** The zones offered once a country is chosen; `UTC` alone for no country. */
export function timezonesForCountry(code) {
    return findCountry(code)?.zones ?? [UTC_ZONE];
}
export function countryName(code) {
    return findCountry(code)?.name;
}
/**
 * `+02:00` — a zone's offset from UTC at a given instant.
 *
 * Computed at `at` rather than cached, because half the world's offsets change
 * twice a year and a picker built in January must not still claim +01:00 in July.
 */
export function timezoneOffset(zone, at = new Date()) {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: zone,
            timeZoneName: 'longOffset',
        }).formatToParts(at);
        const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
        // "GMT+02:00" / "GMT" for UTC itself.
        const offset = name.replace('GMT', '');
        return offset || '+00:00';
    }
    catch {
        return '+00:00';
    }
}
/** Offset in minutes east of UTC — the sortable form of `timezoneOffset`. */
export function timezoneOffsetMinutes(zone, at = new Date()) {
    const match = /^([+-])(\d{2}):(\d{2})$/.exec(timezoneOffset(zone, at));
    if (!match)
        return 0;
    const sign = match[1] === '-' ? -1 : 1;
    return sign * (Number(match[2]) * 60 + Number(match[3]));
}
/** `Europe/Berlin` -> `Berlin` — the part a reader recognises. */
export function timezoneCity(zone) {
    const leaf = zone.split('/').pop() ?? zone;
    return leaf.replace(/_/g, ' ');
}
/** `Berlin (UTC+02:00)` — how a zone reads in a dropdown. */
export function timezoneLabel(zone, at = new Date()) {
    if (zone === UTC_ZONE)
        return 'UTC';
    return `${timezoneCity(zone)} (UTC${timezoneOffset(zone, at)})`;
}
/**
 * Which zone to render someone's clock in.
 *
 * The order is deliberate:
 *
 * 1. An explicit personal choice, once detection is switched off. It is the
 *    only signal that came from a person rather than from a machine.
 * 2. The workspace's declared zone, but *only* for someone who has switched
 *    detection off — they have said their device is not the answer, and a
 *    declared workspace clock is the next best thing.
 * 3. The device. What almost everybody actually gets, including anyone whose
 *    saved zone turns out to be one this runtime cannot format in.
 * 4. The workspace, then UTC, for a runtime with no zone data at all.
 *
 * A saved zone is never used while `auto` is on. Every account created before
 * this setting was real carries `timezone: 'UTC'` as a schema default that
 * nobody chose, and treating those as preferences would move the whole
 * product's clocks to UTC overnight.
 */
export function resolveTimezone(input) {
    const { userTimezone, auto = true, workspaceTimezone, deviceTimezone } = input;
    const chosen = !auto && userTimezone;
    if (chosen && isValidTimezone(userTimezone))
        return userTimezone;
    if (chosen && workspaceTimezone && isValidTimezone(workspaceTimezone))
        return workspaceTimezone;
    const device = deviceTimezone ?? detectTimezone();
    if (device && isValidTimezone(device))
        return device;
    if (workspaceTimezone && isValidTimezone(workspaceTimezone))
        return workspaceTimezone;
    return FALLBACK_TIMEZONE;
}
/**
 * `Intl.DateTimeFormat` is expensive enough to build that the chat log — which
 * formats a timestamp per message — notices. Keyed by zone plus options.
 */
const formatterCache = new Map();
function formatter(zone, options) {
    const key = `${zone}|${JSON.stringify(options)}`;
    let cached = formatterCache.get(key);
    if (!cached) {
        try {
            cached = new Intl.DateTimeFormat(undefined, { ...options, timeZone: zone });
        }
        catch {
            // An unusable saved zone must not blank out every timestamp on screen.
            cached = new Intl.DateTimeFormat(undefined, { ...options, timeZone: FALLBACK_TIMEZONE });
        }
        formatterCache.set(key, cached);
    }
    return cached;
}
/** Formats an instant in `zone`, in the reader's own locale conventions. */
export function formatInZone(value, zone, options) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    return formatter(zone, options).format(date);
}
/**
 * The calendar parts of an instant *as seen in `zone`* — the pieces every
 * "is this today?" and "which day header?" question needs.
 *
 * Reading them off a `Date` directly answers the same question for the
 * device's zone instead, which is the bug this whole module exists to fix.
 */
export function zonedParts(value, zone) {
    const date = value instanceof Date ? value : new Date(value);
    const parts = formatter(zone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const read = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        // Hour 24 is how some locales spell midnight; normalise so comparisons hold.
        hour: read('hour') % 24,
        minute: read('minute'),
    };
}
/** `2026-08-17` — the calendar day an instant falls on in `zone`. */
export function zonedDayKey(value, zone) {
    const { year, month, day } = zonedParts(value, zone);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/** How many calendar days apart two instants are, counted in `zone`. */
export function zonedDayDiff(a, b, zone) {
    const toUtcNoon = (value) => {
        const { year, month, day } = zonedParts(value, zone);
        return Date.UTC(year, month - 1, day);
    };
    return Math.round((toUtcNoon(a) - toUtcNoon(b)) / 86_400_000);
}
/** Hour a day-only phrase ("tomorrow", "friday") lands on: start of work. */
const DEFAULT_HOUR = 9;
/** "tonight" with no time means the evening, not nine in the morning. */
const TONIGHT_HOUR = 20;
const UNIT_MS = {
    m: 60_000,
    h: 3_600_000,
    d: 86_400_000,
    w: 7 * 86_400_000,
};
/** `mins`, `hour`, `days`, `w` → the unit they stand for, or nothing. */
function durationUnit(word) {
    const w = word.toLowerCase();
    if (/^(m|min|mins|minute|minutes)$/.test(w))
        return 'm';
    if (/^(h|hr|hrs|hour|hours)$/.test(w))
        return 'h';
    if (/^(d|day|days)$/.test(w))
        return 'd';
    if (/^(w|wk|wks|week|weeks)$/.test(w))
        return 'w';
    return null;
}
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_WORD = /^(?:(?:on|next)\s+)?(today|tonight|tomorrow|tmr|sun(?:day)?|mon(?:day)?|tue(?:s|sday)?|wed(?:nesday)?|thu(?:rs|rsday)?|fri(?:day)?|sat(?:urday)?)\b\s*/i;
/**
 * `at 15:00`, `9am`, `9:30 pm`, `noon`, `midnight`. A bare number ("9") only
 * counts with an explicit `at`, otherwise "/remind 3 things" would read the
 * three as an hour.
 */
const TIME_WORD = /^(?:(at)\s+)?(?:(noon|midnight)|(\d{1,2})(?::(\d{2}))?\s*(am|pm)?)\b\s*/i;
/**
 * Reads a leading time phrase off a sentence, in the reader's local clock.
 *
 * Understood: `in 20m` / `in 2 hours` / `in 1h30m` / `in 3 days`, `at 15:00`,
 * `3pm`, `tomorrow`, `tomorrow 9am`, `tonight`, `friday at 10`, `next monday`.
 * A time with no day means today, or tomorrow once it has already passed.
 * A day with no time means 9:00.
 *
 * Returns null when the sentence does not start with something it can read,
 * which the caller turns into a usage hint rather than a guess.
 */
export function parseWhen(text, now = new Date()) {
    let input = text.trim();
    /* Relative: "in <n> <unit> [<n> <unit> …]". */
    const relative = /^in\s+/i.exec(input);
    if (relative) {
        let cursor = relative[0].length;
        let total = 0;
        let parts = 0;
        for (;;) {
            const piece = /^(\d+)\s*([a-z]+)\s*(?:and\s+)?/i.exec(input.slice(cursor));
            if (!piece)
                break;
            const unit = durationUnit(piece[2]);
            if (!unit)
                break;
            total += Number(piece[1]) * UNIT_MS[unit];
            cursor += piece[0].length;
            parts += 1;
        }
        if (parts === 0 || total <= 0)
            return null;
        return { at: new Date(now.getTime() + total), rest: stripLead(input.slice(cursor)) };
    }
    /* Absolute: "[day] [at] [time]" in either order of presence. */
    const at = new Date(now);
    at.setSeconds(0, 0);
    let dayGiven = false;
    let evening = false;
    const day = DAY_WORD.exec(input);
    if (day) {
        const word = day[1].toLowerCase();
        dayGiven = true;
        if (word === 'tonight')
            evening = true;
        else if (word === 'tomorrow' || word === 'tmr')
            at.setDate(at.getDate() + 1);
        else if (word !== 'today') {
            const target = WEEKDAYS.indexOf(word.slice(0, 3));
            /* "friday" on a Friday means next week's — today's has already been
               named "today" if that was meant. */
            const ahead = (target - at.getDay() + 7) % 7 || 7;
            at.setDate(at.getDate() + ahead);
        }
        input = input.slice(day[0].length);
    }
    const time = TIME_WORD.exec(input);
    let timeGiven = false;
    if (time) {
        const explicit = Boolean(time[1]) || Boolean(time[2]) || time[4] !== undefined || Boolean(time[5]);
        if (explicit) {
            let hour;
            let minute = 0;
            if (time[2]) {
                hour = time[2].toLowerCase() === 'noon' ? 12 : 0;
            }
            else {
                hour = Number(time[3]);
                minute = Number(time[4] ?? 0);
                const meridiem = time[5]?.toLowerCase();
                if (meridiem) {
                    if (hour < 1 || hour > 12)
                        return null;
                    if (meridiem === 'pm' && hour < 12)
                        hour += 12;
                    if (meridiem === 'am' && hour === 12)
                        hour = 0;
                }
                else if (hour > 23) {
                    return null;
                }
                if (minute > 59)
                    return null;
            }
            at.setHours(hour, minute, 0, 0);
            timeGiven = true;
            input = input.slice(time[0].length);
        }
    }
    if (!dayGiven && !timeGiven)
        return null;
    if (!timeGiven)
        at.setHours(evening ? TONIGHT_HOUR : DEFAULT_HOUR, 0, 0, 0);
    /* "at 9am" said at ten in the morning means tomorrow's nine. */
    if (!dayGiven && at.getTime() <= now.getTime())
        at.setDate(at.getDate() + 1);
    return { at, rest: stripLead(input) };
}
/** "to call Bob" → "call Bob": the connective people type after a time. */
function stripLead(rest) {
    return rest.trim().replace(/^(?:to|that|about)\s+/i, '').trim();
}
